// Inventory allocations — which item is in whose hands for which event — in
// whichever of their four views the caller is.
//
// One component serves all four, because they differ only in what is already
// fixed. Pass the ids the surrounding page has: an item's dialog fixes the item, a
// personnel record fixes the person, an event attendance fixes both the person and
// the event, and the event's own dialog fixes the event. Whatever is fixed stops
// being a column (there is no point repeating it down the page) and stops being a
// question when adding — which is also what narrows the pickers, since /options
// takes the same ids.
//
// The remaining columns are ordered Event, Item, Person, which matches the server's
// ordering in every view: most recent event first, then item category, then item
// name (core/inventory_allocations.sort_key). So the leading column is always the
// one the list is sorted by — unless the reader has chosen another order, which only
// the event view offers (see allocationSort.js).
//
// Rows save as you go, like the event's races and information links, so there is
// nothing here to Save.
//
// A fifth view fixes nothing: `outstanding` lists every row, on every event, that
// still has its item out — the Inventory page's "what hasn't come back?".
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { API_BASE_URL } from "../config";
import { notify } from "../notify";
import { exportAllocations } from "../exportExcel";
import {
  ALLOCATION_SORTS, ALLOCATION_SORT_ITEM, allocationComparator,
} from "../allocationSort";
import {
  ALLOCATION_RETURNED, ALLOCATION_STATUSES, ALLOCATION_STATUS_COLOR,
  INVENTORY_CATEGORY_COLOR,
} from "../constants";

const API = `${API_BASE_URL}/inventory/allocations`;

// `item_ids` rather than one id: a new allocation can be several items to the same
// person at once (kitting somebody out), sent to /bulk. Editing is always one row.
const BLANK = {
  item_ids: [], personnel_id: "", event_id: "",
  status: ALLOCATION_STATUSES[0], notes: "",
};

const EMPTY_OPTIONS = { items: [], people: [], events: [] };

// A row still has the item out unless it has been returned. Mirrors is_open() in
// backend/app/core/inventory_allocations.py, which enforces the one-holder rule;
// here it only decides what to say and what to disable.
const isOpen = (row) => row.status !== ALLOCATION_RETURNED;

// Whether an item is in a person's size. The two vocabularies differ — a person's is
// one of CLOTHING_SIZES ("Men M"), an item's is free text ("Men M 48") — so the
// person's size only has to begin the item's. Note "Men XL" doesn't begin "Men XXL".
const normSize = (s) => (s || "").trim().replace(/\s+/g, " ").toLowerCase();
const sizeMatches = (personSize, itemSize) => {
  const want = normSize(personSize);
  return Boolean(want) && normSize(itemSize).startsWith(want);
};

/**
 * Props:
 *  - itemId / personnelId / eventId: the ids already fixed by the surrounding view.
 *    Pass a number to fix one; anything else (null, undefined, "new") leaves it
 *    open, which is also the guard for a parent dialog that is still fading out.
 *  - canAdd / canEdit / canDelete / canExport: from the InventoryItems permissions —
 *    an allocation is part of an item's record, so it follows the item's rules.
 *  - showStatusFilter / showSort: the event-wide view's Status filter and Sort by.
 *    The other three views are read inside a record whose ordering is fixed.
 *  - exportTitle: what the list is of (an event's label). Set it to offer the Excel
 *    and PDF-checklist exports, which take the rows exactly as ordered and filtered
 *    on screen.
 *  - showReturn: a one-press "Mark returned" beside each open row (needs canEdit),
 *    for the views where kit is checked back in a row at a time.
 *  - outstanding: the cross-event view — every row not yet Returned, with nothing
 *    fixed. Pass false while a parent dialog is closed, as with the ids.
 */
export default function AllocationManager({
  itemId, personnelId, eventId, outstanding,
  canAdd, canEdit, canDelete, canExport, showStatusFilter, showSort, exportTitle,
  showReturn,
}) {
  const fixedItem = typeof itemId === "number";
  const fixedPerson = typeof personnelId === "number";
  const fixedEvent = typeof eventId === "number";
  // Every view fixes at least one id, or asks for everything outstanding. Without
  // either there is nothing to list — and this is what keeps the component quiet
  // while a parent dialog closes.
  const identified = fixedItem || fixedPerson || fixedEvent || Boolean(outstanding);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");        // the optional status filter
  const [sortBy, setSortBy] = useState(ALLOCATION_SORT_ITEM); // ignored unless showSort
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [editing, setEditing] = useState(null);    // null | "new" | id
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [inSize, setInSize] = useState(false);     // item picker: only the person's size

  const load = useCallback(() => {
    if (!identified) { setRows([]); setLoading(true); return Promise.resolve(); }
    const params = {};
    if (fixedItem) params.item_id = itemId;
    if (fixedPerson) params.personnel_id = personnelId;
    if (fixedEvent) params.event_id = eventId;
    if (outstanding) params.outstanding = true;
    if (status) params.status = status;
    return axios.get(API + "/", { params })
      .then(res => setRows(res.data || []))
      .finally(() => setLoading(false));
  }, [identified, fixedItem, fixedPerson, fixedEvent, itemId, personnelId, eventId,
      outstanding, status]);

  useEffect(() => { load(); }, [load]);

  // What is on screen, and what the exports take: the server's ordering unless the
  // reader has chosen one, in which case theirs. The status filter is already applied
  // server-side, so `rows` is the filtered set either way.
  const visible = showSort ? [...rows].sort(allocationComparator(sortBy)) : rows;

  // Which columns are worth showing, in the order the list is sorted by.
  const showEvent = !fixedEvent;
  const showItem = !fixedItem;
  const showPerson = !fixedPerson;
  const columns = [
    showEvent && "event",
    showItem && "item",
    showPerson && "person",
  ].filter(Boolean);
  const canReturn = Boolean(showReturn && canEdit);
  const showActions = canDelete || canReturn;
  const colSpan = columns.length + 1 + (showActions ? 1 : 0); // + status, + actions

  // When the item is fixed, the rows ARE its whole history, so they say on their own
  // whether it is already out — no need to ask /options. In the other views the
  // rows are filtered to a person or an event, so the item picker's `outstanding`
  // is what rules an item out there.
  const alreadyOut = fixedItem ? rows.find(isOpen) : null;

  const openNew = () => {
    setInSize(false);
    setForm({
      ...BLANK,
      item_ids: fixedItem ? [itemId] : [],
      personnel_id: fixedPerson ? personnelId : "",
      event_id: fixedEvent ? eventId : "",
    });
    setEditing("new");
    // Fetched fresh each time rather than once on mount: `outstanding` changes as
    // kit goes out and comes back, and a stale picker would offer an item the
    // server then refuses.
    axios.get(`${API}/options`, {
      params: {
        ...(fixedEvent ? { event_id: eventId } : {}),
        ...(fixedPerson ? { personnel_id: personnelId } : {}),
      },
    }).then(res => setOptions(res.data || EMPTY_OPTIONS))
      .catch(() => setOptions(EMPTY_OPTIONS));
  };

  const openEdit = (row) => {
    if (!canEdit) return;
    setForm({
      item_ids: [row.item_id], personnel_id: row.personnel_id, event_id: row.event_id,
      status: row.status || ALLOCATION_STATUSES[0], notes: row.notes || "",
    });
    setEditing(row.id);
  };

  const close = () => { setEditing(null); setForm(BLANK); };

  const save = () => {
    setBusy(true);
    const notes = form.notes.trim() || null;
    const many = editing === "new" && form.item_ids.length > 1;
    const req = editing === "new"
      ? (many
        // Several items at once go in one all-or-nothing request, with one status
        // and no notes (see InventoryAllocationBulkCreate).
        ? axios.post(`${API}/bulk`, {
            item_ids: form.item_ids, personnel_id: form.personnel_id,
            event_id: form.event_id, status: form.status,
          })
        : axios.post(API + "/", {
            item_id: form.item_ids[0], personnel_id: form.personnel_id,
            event_id: form.event_id, status: form.status, notes,
          }))
      // Only the status and the notes can change — which item, person and event it
      // is are what the allocation is (see InventoryAllocationUpdate).
      : axios.put(`${API}/${editing}`, { status: form.status, notes });
    req.then(() => {
      notify(editing !== "new" ? "Allocation saved."
        : many ? `${form.item_ids.length} allocations added.` : "Allocation added.", "success");
      load();
      close();
    }).catch(() => {}) // the global interceptor surfaces the error
      .finally(() => setBusy(false));
  };

  // One press from the list: the commonest change by far once kit comes back.
  const markReturned = (row) => {
    setBusy(true);
    axios.put(`${API}/${row.id}`, { status: ALLOCATION_RETURNED })
      .then(() => {
        notify(`${row.item?.name || "Item"} marked returned.`, "success");
        load();
      })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  const doDelete = () => {
    setBusy(true);
    axios.delete(`${API}/${confirmDelete.id}`)
      .then(() => {
        notify("Allocation removed.", "success");
        if (editing === confirmDelete.id) close();
        setConfirmDelete(null);
        load();
      })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  // --- Cells ---------------------------------------------------------------
  // One cell per visible column. `withNotes` is set on the leading column only, so
  // the notes are read as part of the row rather than needing a column of their own.
  const cell = (row, key, withNotes) => {
    const notes = withNotes ? (row.notes || "").trim() : "";
    const sub = (parts) => {
      const text = parts.filter(Boolean).join(" — ");
      return text
        ? <Typography variant="caption" color="text.secondary">{text}</Typography>
        : null;
    };
    if (key === "event") {
      return (
        <TableCell key={key}>
          <Typography variant="body2">
            {row.event?.label_long || `Event #${row.event_id}`}
          </Typography>
          {sub([notes])}
        </TableCell>
      );
    }
    if (key === "item") {
      return (
        <TableCell key={key}>
          <Stack direction="row" spacing={1} useFlexGap
                 sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="body2">{row.item?.name || `Item #${row.item_id}`}</Typography>
            {row.item?.category && (
              <Chip size="small" label={row.item.category}
                    color={INVENTORY_CATEGORY_COLOR[row.item.category] || "default"} />
            )}
          </Stack>
          {sub([row.item?.location, notes])}
        </TableCell>
      );
    }
    // Their role on the event this allocation is for, in small print under the name:
    // who someone is on the day is what decides what kit they need, so a list read
    // by person wants it without a column of its own.
    return (
      <TableCell key={key}>
        <Typography variant="body2">
          {row.person?.name || `Person #${row.personnel_id}`}
        </Typography>
        {sub([row.person?.role, notes])}
      </TableCell>
    );
  };

  const heading = { event: "Event", item: "Item", person: "Person" };

  // --- The add / edit form -------------------------------------------------
  const isNew = editing === "new";
  const pickedItems = options.items.filter(o => form.item_ids.includes(o.id));
  const pickedPerson = options.people.find(o => o.id === form.personnel_id) || null;
  const many = isNew && form.item_ids.length > 1;
  const canSave = Boolean(form.item_ids.length && form.personnel_id && form.event_id
                          && form.status) && !busy;

  // The "in their size" filter. Items already picked stay listed whatever their size,
  // or switching the filter on would silently drop them from the selection's chips.
  const personSize = pickedPerson?.clothing_size || "";
  const sizeFilterOn = inSize && Boolean(personSize);
  const itemOptions = sizeFilterOn
    ? options.items.filter(o => sizeMatches(personSize, o.size) || form.item_ids.includes(o.id))
    : options.items;

  // Read-only recap of what an existing allocation is for, since those three can't
  // be changed once it exists.
  const editingRow = isNew ? null : rows.find(r => r.id === editing);

  // --- Exports -------------------------------------------------------------
  // Both take `visible` — the rows as ordered and filtered on screen, since that is
  // what the reader asked for. The PDF says so on its face, so a sheet on a
  // clipboard can't be mistaken for the whole list.
  const exportSubtitle = [
    showSort ? `Ordered by ${sortBy.toLowerCase()}` : null,
    status ? `Status: ${status}` : "All statuses",
  ].filter(Boolean).join("  ·  ");

  const exportPdf = () => import("../exportAllocationsPdf")
    .then(m => m.exportAllocationsPdf({
      allocations: visible, title: exportTitle, subtitle: exportSubtitle,
    }))
    .catch(() => notify("Couldn't build the checklist PDF.", "error"));

  const showExports = Boolean(exportTitle) && canExport;

  return (
    <Box>
      {(showStatusFilter || showSort || showExports) && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap
               sx={{ alignItems: { sm: "center" }, flexWrap: "wrap", mb: 1 }}>
          {showStatusFilter && (
            <TextField label="Status" select size="small" value={status}
                       onChange={e => setStatus(e.target.value)}
                       sx={{ minWidth: 170 }}>
              <MenuItem value=""><em>All statuses</em></MenuItem>
              {ALLOCATION_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          )}
          {showSort && (
            <TextField label="Sort by" select size="small" value={sortBy}
                       onChange={e => setSortBy(e.target.value)}
                       sx={{ minWidth: 150 }}>
              {ALLOCATION_SORTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          )}
          {showExports && (
            <Stack direction="row" spacing={1} useFlexGap
                   sx={{ alignItems: "center", flexWrap: "wrap", ml: { sm: "auto" } }}>
              <Tooltip title="The rows as ordered and filtered here">
                <span>
                  <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                          disabled={visible.length === 0}
                          onClick={() => exportAllocations(visible, { title: exportTitle })}>
                    Export to Excel
                  </Button>
                </span>
              </Tooltip>
              {/* The printed form for the day: this ordering, with a box to tick as
                  each thing goes out and another as it comes back. */}
              {/* One event's sheet: it prints the event as its badge and has no Event
                  column, so the cross-event view offers the Excel export alone. */}
              {fixedEvent && (
                <Tooltip title="Printable checklist in this order, with Given out / Returned boxes to tick">
                  <span>
                    <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />}
                            disabled={visible.length === 0} onClick={exportPdf}>
                      Checklist PDF
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>
      )}

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                {columns.map(key => <TableCell key={key}>{heading[key]}</TableCell>)}
                <TableCell>Status</TableCell>
                {showActions && <TableCell align="right" padding="none" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map(row => (
                <TableRow hover key={row.id} onClick={() => openEdit(row)}
                          sx={{ cursor: canEdit ? "pointer" : "default" }}>
                  {columns.map((key, i) => cell(row, key, i === 0))}
                  <TableCell>
                    <Chip size="small" label={row.status}
                          color={ALLOCATION_STATUS_COLOR[row.status] || "default"} />
                  </TableCell>
                  {showActions && (
                    <TableCell align="right" padding="none" sx={{ whiteSpace: "nowrap", pr: 1 }}>
                      {canReturn && isOpen(row) && (
                        <Tooltip title="Mark returned">
                          <IconButton size="small" color="success" disabled={busy}
                                      aria-label={`Mark ${row.item?.name || "item"} returned`}
                                      onClick={(e) => { e.stopPropagation(); markReturned(row); }}>
                            <AssignmentReturnIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error"
                                      aria-label={`Remove allocation of ${row.item?.name || "item"}`}
                                      onClick={(e) => { e.stopPropagation(); setConfirmDelete(row); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center"
                             sx={{ color: "text.secondary", py: 2 }}>
                    {loading ? <CircularProgress size={24} />
                      : status ? `Nothing with the status "${status}".`
                        : outstanding ? "Everything allocated has been returned."
                          : "No allocations."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {canAdd && (
        <Tooltip title={alreadyOut
          ? `${alreadyOut.item?.name || "This item"} is still out to `
            + `${alreadyOut.person?.name || "someone"} — mark that Returned first.`
          : ""}>
          <span>
            <Button size="small" startIcon={<AddIcon />} onClick={openNew}
                    disabled={Boolean(alreadyOut)} sx={{ mt: 1 }}>
              Add allocation
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Add / edit one allocation */}
      <Dialog open={editing !== null} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>{isNew ? "Add allocation" : "Edit allocation"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!isNew && (
              <DialogContentText variant="body2">
                {[editingRow?.item?.name, editingRow?.person?.name,
                  editingRow?.event?.label_long].filter(Boolean).join(" · ")
                  || "This allocation"}
                . To change the item, the person or the event, remove this and add
                the right one — those three are what the allocation is.
              </DialogContentText>
            )}

            {/* Person and event come before the items: kitting somebody out starts with
                who, and the size filter below needs to know whose size. */}
            {isNew && showPerson && (
              <Autocomplete
                options={options.people}
                getOptionLabel={(o) => o.name || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={pickedPerson}
                onChange={(_, v) => setForm(f => ({ ...f, personnel_id: v ? v.id : "" }))}
                renderInput={(params) => (
                  <TextField {...params} label="Person" required
                             helperText={!fixedEvent ? undefined
                               : options.people.length
                                 ? "The people on this event."
                                 : "Nobody is on this event yet — add them to its "
                                   + "personnel list first."} />
                )}
              />
            )}

            {isNew && showEvent && (
              <TextField label="Event" select fullWidth required value={form.event_id}
                         onChange={e => setForm(f => ({ ...f, event_id: e.target.value }))}
                         helperText={fixedPerson ? "The events this person is on." : undefined}>
                {options.events.map(ev => (
                  <MenuItem key={ev.id} value={ev.id}>{ev.label_long}</MenuItem>
                ))}
              </TextField>
            )}

            {isNew && showItem && (
              <Box>
                {/* Several items may be chosen, all going to the same person for the
                    same event in one save. */}
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={itemOptions}
                  groupBy={(o) => o.category || "Uncategorised"}
                  getOptionLabel={(o) => o.name || ""}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  // An item that is still out can't be allocated again — the server
                  // refuses it, so it is shown with its holder rather than offered.
                  getOptionDisabled={(o) => Boolean(o.outstanding)}
                  value={pickedItems}
                  onChange={(_, v) => setForm(f => ({ ...f, item_ids: v.map(o => o.id) }))}
                  renderOption={({ key, ...props }, o) => (
                    <li key={key} {...props}>
                      <Box>
                        <Typography variant="body2">{o.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {o.outstanding ? `Out to ${o.outstanding}` : (o.location || "—")}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Items" required={!form.item_ids.length}
                               helperText={options.items.length
                                 ? "Choose one or more. Only types ticked \"Can be "
                                   + "allocated\" are offered."
                                 : "No inventory types are marked as allocatable yet — "
                                   + "tick \"Can be allocated\" on a type first."} />
                  )}
                />
                <Tooltip title={personSize ? ""
                  : pickedPerson ? "No clothing size is recorded for this person."
                    : "Choose the person first."}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={sizeFilterOn}
                                       disabled={!personSize}
                                       onChange={e => setInSize(e.target.checked)} />}
                    label={<Typography variant="body2">
                      Only items in their size{personSize ? ` (${personSize})` : ""}
                    </Typography>} />
                </Tooltip>
              </Box>
            )}

            <TextField label="Status" select fullWidth required value={form.status}
                       onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                       helperText={(many ? "The same for every item chosen. " : "")
                                   + "Assigned when it's written down, Given once it's handed "
                                   + "over, Returned when it's back — only Returned frees "
                                   + "the item for someone else."}>
              {ALLOCATION_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>

            {/* A note belongs to one item, so it's added to that row afterwards when
                several are allocated at once. */}
            {!many && (
              <TextField label="Notes" fullWidth multiline minRows={2} value={form.notes}
                         onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>Cancel</Button>
          <Button variant="contained" disabled={!canSave} onClick={save}>
            {many ? `Allocate ${form.item_ids.length} items` : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Remove allocation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the record that {confirmDelete?.person?.name || "this person"} has
            {" "}{confirmDelete?.item?.name || "this item"} for
            {" "}{confirmDelete?.event?.label_long || "this event"}. Nothing else changes —
            an allocation never held any stock. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={busy}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />}
                  onClick={doDelete} disabled={busy}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
